"use client";

import { useState, useEffect, useCallback } from "react";
import { collection, query, where, onSnapshot, orderBy, doc, updateDoc, writeBatch, limit } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Notificacion } from "@/types/notificacion";
import { useAuth } from "@/context/AuthContext";

export function useNotifications() {
  const { usuarioRPA } = useAuth();
  const [notificaciones, setNotificaciones] = useState<Notificacion[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!usuarioRPA?.uid) {
      // Limpieza al cerrar sesión: evita mostrar notificaciones del
      // usuario anterior mientras no hay suscripción activa a Firestore.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNotificaciones([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "notificaciones"),
      where("usuarioDestinoUid", "==", usuarioRPA.uid),
      orderBy("fecha", "desc"),
      limit(100),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data: Notificacion[] = [];
        snapshot.forEach((d) => {
          data.push({ id: d.id, ...d.data() } as Notificacion);
        });
        setNotificaciones(data);
        setUnreadCount(data.filter(n => !n.leida).length);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching notificaciones:", err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [usuarioRPA?.uid]);

  const markAsRead = useCallback(async (notifId: string) => {
    try {
      await updateDoc(doc(db, "notificaciones", notifId), { leida: true });
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!usuarioRPA?.uid) return;
    try {
      const batch = writeBatch(db);
      notificaciones
        .filter(n => !n.leida)
        .forEach(n => {
          batch.update(doc(db, "notificaciones", n.id), { leida: true });
        });
      await batch.commit();
    } catch (err) {
      console.error("Error marking all as read:", err);
    }
  }, [notificaciones, usuarioRPA?.uid]);

  return { notificaciones, unreadCount, loading, markAsRead, markAllAsRead };
}
